/**
 * Client-side watchdog budgets for the AskGemini chat SSE stream (SE-7078).
 * @remarks
 * The chat stream used to have no client-side time budget at all: `useSSE` and
 * `useChat` cleared `isLoadingVar` only on a `complete` / `end` / `error` SSE
 * frame, or on the EventSource's own `close` / `error`. That was a deliberate
 * "trust the backend to terminate the stream" decision, and it holds right up
 * until the stream stops without a terminal frame — at which point the panel
 * spins forever with no error and no way back. That is exactly what was
 * reported: the panel cycling between "Understanding your question" and
 * "Finding the right data" indefinitely.
 *
 * These two budgets replace that unconditional trust. They are deliberately
 * generous: cutting a slow-but-healthy answer short is a worse failure than
 * making the user wait, so both values are derived from measured behaviour of
 * the live handler rather than picked for snappiness.
 *
 * **Where the numbers come from.** SE-7401 traced the live stream to
 * `UnifiedStreamingLambda` (CFN stack `<env>-SseStreamStack`, from
 * `geminisportsai/player-graphrag-graph-bot`) and measured it across six live
 * reproductions plus fourteen days of dev and staging CloudWatch logs:
 *
 * - The Lambda's own hard timeout is **300 s**. No legitimate frame can arrive
 *   later than that after invocation, so a stream still silent past that point
 *   is provably dead rather than slow.
 * - The longest silent window *within* a healthy stream is **20–24 s**, during
 *   each LLM SQL generation.
 * - The slowest healthy user-visible answer observed was **~133 s** (five SQL
 *   retry rounds). The ~150 s+ figures in the CloudWatch REPORT lines are not
 *   user-visible: every invocation lingers a constant ~120 s after the terminal
 *   frame on a LangSmith trace upload.
 * - Post-deploy verification on dev added a load dimension: five runs, all
 *   terminating cleanly, at 32.8 s (the reported prompt), 32.3 s (named
 *   control), 45.9 s (5,764-char prompt), 33.2 s and **58.7 s** under load.
 *   Latency stretched ~1.8x under contention while termination stayed clean, so
 *   any budget below ~60 s would manufacture false failures during our own E2E
 *   windows — the self-inflicted-failure pattern SE-7424 documents.
 *
 * **App backgrounding on native — closed by SE-7443.** Both budgets are plain
 * `setTimeout`s, and React Native suspends JS while the app is backgrounded,
 * firing expired timers on resume. A user who backgrounded the app mid-answer
 * for longer than the silence budget therefore came back to an already-expired
 * watchdog, and a healthy stream was torn down with "the assistant stopped
 * responding". SE-7443 fixed that by changing what the silence budget measures
 * rather than how big it is: `useSSE` now keeps a `ForegroundClock`
 * (`./foregroundClock`) that discounts backgrounded time, and the watchdog
 * re-arms for the remaining foreground budget instead of firing. **The numbers
 * below are unchanged and must stay that way** — lengthening a budget to
 * survive a background would hide the false failure while making a genuinely
 * dead stream take proportionally longer to detect.
 *
 * The **deadline is deliberately still wall-clock** and is NOT extended by the
 * backgrounded duration: it bounds the handler Lambda's work, and that Lambda
 * keeps burning its own 300 s clock while the phone is asleep. A stream still
 * open past this deadline is dead whether or not anyone was watching, so
 * firing it on resume is the correct outcome.
 *
 * **The success terminator is `end` followed by `data: [DONE]`, not a frame
 * named `complete`** — `complete` is documented in the handler but has zero
 * call sites and is never emitted. `useChat` accepts either, so nothing here
 * depends on the distinction; it is recorded because a terminator keyed on
 * `complete` alone would hang on a perfectly healthy stream.
 * @see {@link https://linear.app/gemini-sports/issue/SE-7078} — this fix
 * @see {@link https://linear.app/gemini-sports/issue/SE-7401} — the trace the
 * numbers above come from
 * @see {@link https://linear.app/gemini-sports/issue/SE-7443} — the
 * backgrounding fix
 */

/**
 * How long the stream may go completely silent before it is declared dead.
 * @remarks
 * Reset by every frame the stream produces — `open`, `start`, `status`,
 * `token`, `citations`, anything. This is the guard that catches the reported
 * bug: a stream killed or dropped mid-answer simply stops producing frames, so
 * silence is the only signal the client gets.
 *
 * 90 s is ~3.75x the worst silent window measured inside a healthy stream
 * (20–24 s during SQL generation). Even the pathological five-retry-round
 * request never came close to a 90 s gap between frames, so a trip here means
 * the stream really has stopped rather than that it is thinking hard.
 *
 * It also clears the ~60 s floor the load measurements imply with room to
 * spare: at the observed ~1.8x stretch under contention a 24 s window becomes
 * ~43 s, still only half this budget. Do not lower it below 60 s.
 */
export const STREAM_SILENCE_TIMEOUT_MS = 90_000;

/**
 * Absolute ceiling on a single answer, measured from the moment the request is
 * dispatched.
 * @remarks
 * A backstop rather than the primary guard. The silence budget only bounds a
 * stream that goes *quiet*; it does nothing about one that keeps talking
 * without ever finishing. The live handler retries failed SQL generation in a
 * loop, emitting a `status` frame each round — every one of those resets the
 * silence budget, so a retry loop that never converges would spin forever
 * under the silence guard alone. This deadline is what bounds it.
 *
 * A note for whoever wires up SE-7408's planned heartbeat: it will only reset
 * the silence budget if it arrives as one of the named events `useSSE` listens
 * for AND carries a data payload. `react-native-sse` drops an event with no
 * registered handler rather than falling back to `message`, and ignores frames
 * with an empty body — so a conventional SSE comment heartbeat (`: ping`) is
 * invisible to this client. Add the event name to `namedEvents` in `useSSE`
 * when that lands, or the heartbeat will not do what it looks like it does.
 *
 * 330 s = the handler Lambda's 300 s hard timeout plus 30 s of margin for
 * connection setup and in-flight delivery. It is ~2.5x the slowest healthy
 * answer ever measured (~133 s), so it cannot cut off a real one.
 */
export const STREAM_DEADLINE_MS = 330_000;

/**
 * Why a stream watchdog fired.
 */
export type StreamTimeoutReason = "silence" | "deadline";

/**
 * User-facing copy for a stream that timed out.
 * @param reason - Which watchdog fired
 * @param hasPartialAnswer - Whether some of the answer already rendered
 * @returns A message that explains the stall and points at the way out
 * @remarks
 * The chat surface renders the error reactive variable as a plain banner with
 * no retry button, so the retry affordance has to live in the sentence itself —
 * the user needs to be told that re-sending is the move.
 *
 * `hasPartialAnswer` exists because "send your question again" is actively
 * wrong when an answer is already sitting on screen. The common case is a
 * stream that dies after some tokens have rendered: the user has partial but
 * real output, and telling them the assistant never responded contradicts what
 * they can see.
 *
 * It is also the safety net for a terminator this client failed to recognise.
 * `useSSE` now honours a terminal event NAME even when its payload does not
 * parse, which closes the known instance of that; this copy is what keeps any
 * remaining instance from being reported as a total failure over good output.
 */
export const streamTimeoutMessage = (
  reason: StreamTimeoutReason,
  hasPartialAnswer: boolean = false
): string => {
  if (hasPartialAnswer) {
    return "The connection ended before the assistant signalled it had finished, so this answer may be incomplete. Send your question again if something is missing.";
  }

  return reason === "silence"
    ? "The assistant stopped responding before it finished answering. Please send your question again."
    : "This question took too long to answer and was stopped. Please try again, or ask a narrower version of the question.";
};
